import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { api } from '@/lib/api.js';
import { DataTable } from '@/components/DataTable.jsx';
import { ConfirmDialog } from '@/components/ConfirmDialog.jsx';
import { PageHeader } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { Input, FormField, Textarea } from '@/components/ui/Input.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { Plus, Pencil, Trash2 } from 'lucide-react';

/**
 * Generic master CRUD page. Provide:
 *  - title, description, endpoint (e.g. '/departments'), queryKey
 *  - fields: [{ name, label, type?, required?, default?, placeholder? }]
 *  - columns: DataTable columns (without actions; this builds them)
 *  - serialize?: (formValues) => payload (default identity)
 *  - hydrate?: (row) => formValues (default identity)
 */
export function MasterCrudPage({
  title, description, endpoint, queryKey, fields, columns,
  serialize = (v) => v, hydrate = (r) => r, extraActions, filterFn,
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [confirmId, setConfirmId] = React.useState(null);

  const { data = [], isLoading } = useQuery({ queryKey, queryFn: () => api.get(endpoint) });

  const form = useForm({});
  React.useEffect(() => {
    if (open) form.reset(editing ? hydrate(editing) : Object.fromEntries(fields.map((f) => [f.name, f.default ?? ''])));
  }, [open, editing]); // eslint-disable-line

  const saveMut = useMutation({
    mutationFn: async (vals) => {
      const payload = serialize(vals);
      if (editing) return api.put(`${endpoint}/${editing.id}`, payload);
      return api.post(endpoint, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      setOpen(false);
      toast({ variant: 'success', title: editing ? 'Updated' : 'Created' });
    },
    onError: (e) => toast({ variant: 'error', title: 'Save failed', description: e.message }),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.del(`${endpoint}/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); toast({ variant: 'success', title: 'Deleted' }); },
    onError: (e) => toast({ variant: 'error', title: 'Delete failed', description: e.message }),
  });

  const finalCols = [
    ...columns,
    {
      key: '__actions',
      header: '',
      className: 'w-28 text-right',
      render: (row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => { setEditing(row); setOpen(true); }} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={() => setConfirmId(row.id)} aria-label="Delete"><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title={title} description={description} actions={
        <>{extraActions}<Button onClick={() => { setEditing(null); setOpen(true); }}><Plus className="h-4 w-4" />New</Button></>
      } />

      <DataTable columns={finalCols} data={filterFn ? data.filter(filterFn) : data} loading={isLoading} exportFilename={`${queryKey}.csv`} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'Create'} {title.replace(/s$/, '')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={form.handleSubmit((v) => saveMut.mutate(v))} className="space-y-3">
            {fields.map((f) => (
              <FormField key={f.name} label={f.label} required={f.required} error={form.formState.errors?.[f.name]?.message}>
                {f.type === 'textarea' ? (
                  <Textarea {...form.register(f.name, { required: f.required && 'Required' })} placeholder={f.placeholder} />
                ) : f.type === 'select' ? (
                  <select className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100"
                    {...form.register(f.name, { required: f.required && 'Required' })}>
                    {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <Input
                    type={f.type || 'text'}
                    step={f.type === 'number' ? (f.step || '1') : undefined}
                    placeholder={f.placeholder}
                    {...form.register(f.name, {
                      required: f.required && 'Required',
                      valueAsNumber: f.type === 'number',
                    })}
                  />
                )}
              </FormField>
            ))}
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button type="submit" disabled={saveMut.isPending}>{saveMut.isPending ? 'Saving…' : 'Save'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmId != null}
        onOpenChange={(o) => !o && setConfirmId(null)}
        title="Delete record?"
        description="This action cannot be undone."
        confirmText="Delete"
        onConfirm={() => deleteMut.mutate(confirmId)}
      />
    </div>
  );
}
